import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon, IconButton } from '@cbrebuild/blocks';
import Select from '../../../nucleus/select/select';
import AddItem from '../../../nucleus/add-item/add-item';
import { convertFreeRentMonthsToYearsAndMonths, getUpdatedFreeRentTotalMonths } from '../../../utils/free-rent-utils';
import Datepicker from '../../../nucleus/datepicker/datepicker';
import NumberInput from '../../../nucleus/inputs/NumberInput';
import {
  FIELD_KEY_HAS_COMMISSION_AGREEMENT,
  FIELD_KEY_COMMISSION_AGREEMENT,
  COMMISSION_ATTACHMENT_KEY,
  DOC_TYPE_COMMISSION_AGREEMENT,
  DOC_TYPE_OTHER,
  SQUARE_FOOTAGE_FIELD_KEYS,
} from '../voucher-verify-constants';
import {
  checkFieldOutlier,
  getOutlierRangeLabel,
} from '../voucher-verify-utils';
import CommissionAttachmentSelector from './CommissionAttachmentSelector';

const FreeRentInput = ({ value, onChange, disabled }) => {
  const years = value?.years ?? 0;
  const months = value?.months ?? 0;

  return (
    <div className="free-rent-inputs">
      <NumberInput
        name="free-rent-years"
        value={years}
        precision={0}
        integerPrecision={4}
        disabled={disabled}
        onChange={e => onChange({ years: parseInt(e.target.value, 10) || 0, months })}
      />
      <span>Years</span>
      <NumberInput
        name="free-rent-months"
        value={months}
        precision={0}
        integerPrecision={2}
        disabled={disabled}
        onChange={(e) => {
          // Months is 0-11 -- carry any overflow (e.g. typing 16) into Years, same
          // as the Comp Data screen's stored total_months / 12 normalization.
          const rawMonths = parseInt(e.target.value, 10) || 0;
          onChange({ years: years + Math.floor(rawMonths / 12), months: rawMonths % 12 });
        }}
      />
      <span>Months</span>
    </div>
  );
};

const RadioGroup = ({ value, onChange, disabled }) => (
  <div className="radio-group">
    <label>
      <input type="radio" checked={value === true} disabled={disabled} onChange={() => onChange(true)} />
      Yes
    </label>
    <label>
      <input type="radio" checked={value === false} disabled={disabled} onChange={() => onChange(false)} />
      No
    </label>
  </div>
);

const toNumberInputValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(numericValue) ? null : numericValue;
};

const isLeaseEscalation = key => key === 'rent_escalation_is_custom' || key === 'lease_escalation';

const EscalationTypeToggle = ({ isPercent, onChange }) => (
  <Select
    key={isPercent ? 'percent' : 'dollar'}
    className="escalation-type-select"
    defaultOption={{ value: isPercent ? '%' : '$', display_name: isPercent ? '%' : '$' }}
    options={[{ value: '$', display_name: '$' }, { value: '%', display_name: '%' }]}
    onChange={option => onChange(option.value === '%')}
  />
);

const LeaseEscalationInput = ({ value, onChange }) => {
  const isPercent = value?.rent_escalation_is_percent === true
    || String(value?.rent_escalation_is_percent).toLowerCase() === 'true';
  const valueKey = isPercent ? 'rent_escalation_percent' : 'rent_escalation_dollar';

  return (
    <div className="lease-escalation-fixed">
      <EscalationTypeToggle
        isPercent={isPercent}
        onChange={(nextIsPercent) => {
          const activeKey = nextIsPercent ? 'rent_escalation_percent' : 'rent_escalation_dollar';
          const inactiveKey = nextIsPercent ? 'rent_escalation_dollar' : 'rent_escalation_percent';
          onChange({
            ...value,
            rent_escalation_is_percent: nextIsPercent,
            [activeKey]: value?.[activeKey] ?? null,
            [inactiveKey]: 0,
          });
        }}
      />
      <NumberInput
        name={valueKey}
        value={toNumberInputValue(value?.[valueKey])}
        precision={isPercent ? 8 : 2}
        integerPrecision={11}
        onChange={(event) => {
          const inactiveKey = isPercent ? 'rent_escalation_dollar' : 'rent_escalation_percent';
          onChange({
            ...value,
            [valueKey]: event.target.value,
            [inactiveKey]: 0,
          });
        }}
      />
    </div>
  );
};

const emptyEscalationRow = () => ({ months: null, is_percent: false, rent_dollar: null, rent_percent: null });

const LeaseEscalationSchedule = ({ rows, onChange, onCommit }) => {
  const updateRow = (index, patch) => {
    onChange(rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };

  return (
    <div className="lease-escalation-schedule">
          {rows.map((row, index) => {
            const { years, months } = convertFreeRentMonthsToYearsAndMonths(row.months);
            const rentMissing = (row.rent_dollar == null || row.rent_dollar === '')
              && (row.rent_percent == null || row.rent_percent === '');
            const monthsMissing = row.months == null || row.months === '';
            const isPercent = row.is_percent === true || String(row.is_percent).toLowerCase() === 'true';
            const valueKey = isPercent ? 'rent_percent' : 'rent_dollar';
            return (
              <div className="lease-escalation-schedule-row" key={`escalation-${index}`}>
                <div className={rentMissing ? 'has-error' : ''}>
                  <span className="lease-escalation-row-label">{`Rent ${index + 1}`}</span>
                  <div className="lease-escalation-fixed">
                    <EscalationTypeToggle
                      isPercent={isPercent}
                      onChange={nextIsPercent => updateRow(index, { is_percent: nextIsPercent })}
                    />
                    <NumberInput
                      name={`rent-${index}`}
                      value={toNumberInputValue(row[valueKey])}
                      precision={isPercent ? 8 : 2}
                      integerPrecision={11}
                      onChange={event => updateRow(index, { [valueKey]: event.target.value })}
                    />
                  </div>
                  {rentMissing && <span className="required-error-message">Required</span>}
                </div>
                <div className={monthsMissing ? 'has-error' : ''}>
                  <span className="lease-escalation-row-label">Year</span>
                  <NumberInput
                    name={`years-${index}`}
                    value={toNumberInputValue(years)}
                    precision={0}
                    integerPrecision={4}
                    onChange={(event) => {
                      updateRow(index, { months: getUpdatedFreeRentTotalMonths({
                        currentTotalMonths: row.months,
                        part: 'years',
                        value: event.target.value,
                      }) });
                    }}
                  />
                  {monthsMissing && <span className="required-error-message">Year is required</span>}
                </div>
                <div className={monthsMissing ? 'has-error' : ''}>
                  <span className="lease-escalation-row-label">Month</span>
                  <NumberInput
                    name={`months-${index}`}
                    value={toNumberInputValue(months)}
                    precision={0}
                    integerPrecision={2}
                    onChange={(event) => {
                      updateRow(index, { months: getUpdatedFreeRentTotalMonths({
                        currentTotalMonths: row.months,
                        part: 'months',
                        value: event.target.value,
                      }) });
                    }}
                  />
                  {monthsMissing && <span className="required-error-message">Month is required</span>}
                </div>
                  <IconButton
                    className="lease-escalation-remove"
                    iconName="close-circle"
                    variant="basic"
                    aria-label={`Remove Rent ${index + 1}`}
                    onClick={() => {
                      const nextRows = rows.filter((_, rowIndex) => rowIndex !== index);
                      onChange(nextRows);
                      onCommit(nextRows);
                    }}
                  />
              </div>
            );
          })}
      <AddItem label="Add Rent" onClick={() => onChange([...rows, emptyEscalationRow()])} />
    </div>
  );
};

const LeaseEscalationEditor = ({ value, savedValues, onChange, onModeChange, onScheduleCommit }) => {
  const type = Array.isArray(value) ? 'custom' : value && typeof value === 'object' ? 'fixed' : '';
  const [fixedValue, setFixedValue] = useState(() => (
    value && typeof value === 'object' && !Array.isArray(value)
      ? value
      : savedValues?.fixed || { rent_escalation_dollar: null, rent_escalation_percent: null, rent_escalation_is_percent: true }
  ));
  const [customValue, setCustomValue] = useState(() => (
    Array.isArray(value) ? value : (Array.isArray(savedValues?.custom) ? savedValues.custom : [emptyEscalationRow()])
  ));

  useEffect(() => {
    if (Array.isArray(value)) setCustomValue(value);
    else if (value && typeof value === 'object') setFixedValue(value);
  }, [value]);

  useEffect(() => {
    if (savedValues?.fixed) setFixedValue(savedValues.fixed);
    if (Array.isArray(savedValues?.custom)) setCustomValue(savedValues.custom);
  }, [savedValues]);

  const updateValue = (nextValue) => {
    if (Array.isArray(nextValue)) setCustomValue(nextValue);
    else if (nextValue && typeof nextValue === 'object') setFixedValue(nextValue);
    onChange(nextValue);
  };

  const handleTypeChange = (option) => {
    if (option.value === type) return;
    if (option.value === 'fixed') {
      updateValue(fixedValue);
      onModeChange(false, fixedValue);
    } else if (option.value === 'custom') {
      updateValue(customValue);
      onModeChange(true, customValue);
    } else {
      onChange('');
    }
  };

  return (
    <div className="lease-escalation-editor">
      <Select
        key={type}
        className="lease-escalation-mode-select"
        defaultOption={{ value: type, display_name: type === 'fixed' ? 'Fixed' : type === 'custom' ? 'Custom' : '' }}
        options={[{ value: 'fixed', display_name: 'Fixed' }, { value: 'custom', display_name: 'Custom' }]}
        onChange={handleTypeChange}
      />
      {type === 'fixed' && (
        <>
          <div className="lease-escalation-detail-label">Escalation (Per Year)</div>
          <LeaseEscalationInput value={value} onChange={updateValue} />
        </>
      )}
      {type === 'custom' && (
        <>
          <div className="lease-escalation-detail-label">Lease Escalation Breakdown</div>
          <LeaseEscalationSchedule rows={value} onChange={updateValue} onCommit={onScheduleCommit} />
        </>
      )}
    </div>
  );
};

const FieldRow = ({
  field,
  fieldValues,
  onChange,
  onBooleanChange,
  onEscalationModeChange,
  onEscalationScheduleCommit,
  onDateCommit,
  onBlur,
  dealId,
  attachments,
  onAttachmentsChange,
  outlierRanges,
  isSaving,
  requiredErrorMessage,
  emptyReason,
  isRequired,
}) => {
  const {
    key,
    label,
    isEditable,
    fieldType,
    currentValue,
    isLocked,
    outlierLabel,
    outlierMin,
    outlierMax,
  } = field;

  const displayValue = fieldValues[key] !== undefined ? fieldValues[key] : currentValue;
  const displayLabel = isLeaseEscalation(key)
    ? 'Lease Escalation'
    : String(label || '').replace(/^\*\s*/, '');

  const outlierKey = isLeaseEscalation(key)
    ? (displayValue?.rent_escalation_is_percent === true || String(displayValue?.rent_escalation_is_percent).toLowerCase() === 'true'
      ? 'rent_escalation_percent'
      : 'rent_escalation_dollar')
    : key;
  const showOutlier = checkFieldOutlier(field, displayValue, outlierRanges);
  const showWarningIcon = showOutlier || (key === 'lease_term_months' && Boolean(emptyReason));
  const range = outlierRanges[outlierKey] || (
    outlierMin !== null && outlierMin !== undefined && outlierMax !== null && outlierMax !== undefined
      ? { min: outlierMin, max: outlierMax }
      : null
  );
  const liveBoundsLabel = getOutlierRangeLabel(outlierKey, fieldType, range);
  const effectiveOutlierLabel = liveBoundsLabel || outlierLabel || null;
  // matches the Review tab's fixed wording (RequiredLabeledItem.js) instead of the
  // legacy per-field backend outlierMessage, so the same field reads the same on both pages
  const effectiveOutlierMessage = showOutlier
    ? 'These values are considered outliers. Please validate'
    : null;

  const renderInput = () => {
    if (!isEditable) {
      if (fieldType === 'date' && displayValue) {
        const d = new Date(`${displayValue}T00:00:00`);
        const formatted = Number.isNaN(d.getTime()) ? displayValue
          : d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
        return <span className="field-value">{formatted}</span>;
      }
      let readOnlyValue = String(displayValue ?? '');
      if (key === 'lease_term_months'
        && readOnlyValue && !/month/i.test(readOnlyValue)) {
        readOnlyValue = `${readOnlyValue} Months`;
      }
      // Term can't be computed when its underlying start/expiration dates are unavailable --
      // show the app's standard "no value" placeholder instead of a blank row.
      if (!readOnlyValue) {
        readOnlyValue = '—';
      }

      return <span className="field-value">{readOnlyValue}</span>;
    }

    if (fieldType === 'date') {
      return (
        <Datepicker
          date={displayValue ?? ''}
          onDateChange={val => onChange(key, val)}
          onDayPickerHide={val => onDateCommit(key, val)}
          disabled={false}
        />
      );
    }

    if (fieldType === 'boolean') {
      return <RadioGroup value={displayValue} onChange={val => onBooleanChange(key, val)} />;
    }

    if (fieldType === 'free_rent') {
      return <FreeRentInput value={displayValue} onChange={val => onChange(key, val)} />;
    }

    if (isLeaseEscalation(key)) {
      return (
        <LeaseEscalationEditor
          value={displayValue}
          savedValues={field.escalationValues}
          onChange={value => onChange(key, value)}
          onModeChange={(isCustom, value) => onEscalationModeChange(key, isCustom, value)}
          onScheduleCommit={value => onEscalationScheduleCommit(key, value)}
        />
      );
    }

    if (fieldType === 'decimal') {
      // Match the Angular Comp Data screen (lease_comp.tpl.jade + human-number directive)
      // exactly: currency fields (base_rent, TIA, operating expenses, rent_escalation_dollar)
      // use data-human-number-currency="true" -> 2dp; rent_escalation_percent uses
      // data-human-number-rounding="8" -> 8dp; lease_term_months (Term) is whole months.
      let precision = 2;
      if (key === 'lease_term_months') {
        precision = 0;
      } else if (key === 'rent_escalation_percent') {
        precision = 8;
      } else if (SQUARE_FOOTAGE_FIELD_KEYS.includes(key)) {
        precision = 3;
      }
      return (
        <NumberInput
          name={key}
          value={toNumberInputValue(displayValue)}
          precision={precision}
          integerPrecision={SQUARE_FOOTAGE_FIELD_KEYS.includes(key) ? 13 : 11}
          onChange={e => onChange(key, e.target.value)}
        />
      );
    }

    return (
      <input
        className="field-input"
        type="text"
        value={displayValue ?? ''}
        onChange={e => onChange(key, e.target.value)}
      />
    );
  };

  const showCommissionAttachments = (
    key === FIELD_KEY_HAS_COMMISSION_AGREEMENT || key === FIELD_KEY_COMMISSION_AGREEMENT
  ) && typeof displayValue === 'boolean';
  const showRequiredError = requiredErrorMessage
    && !(isLeaseEscalation(key) && Array.isArray(displayValue) && displayValue.length > 0);

  return (
    <>
      <div
        className={`field-row${isLocked ? ' readonly' : ''}${showOutlier ? ' has-outlier' : ''}${showRequiredError ? ' has-error' : ''}`}
        onBlur={(event) => {
          if (event.target.matches('input[type="radio"]')) return;
          if (event.target.closest('.lease-escalation-mode-select')) return;
          onBlur(key);
        }}
      >
        <span className="field-label">
          {showWarningIcon && <Icon iconName="warning" variant="warning" size="medium" className="outlier-icon" />}
          {displayLabel}
          {isRequired && <span className="required-indicator">*</span>}
        </span>
        <div className="field-right">
          {renderInput()}
          {isSaving && <span className="field-saving-message">Saving...</span>}
          {!displayValue && emptyReason && (
            <div className="field-empty-reason">{emptyReason}</div>
          )}
          {showRequiredError && (
            <div className="required-error-message">{requiredErrorMessage}</div>
          )}
          {showOutlier && (effectiveOutlierLabel || effectiveOutlierMessage) && (
            <div className="outlier-warning">
              {effectiveOutlierLabel && <span className="outlier-label">{effectiveOutlierLabel}</span>}
              {effectiveOutlierMessage && <span className="outlier-message">{effectiveOutlierMessage}</span>}
            </div>
          )}
        </div>
      </div>

      {showCommissionAttachments && (
        <div className="attachment-section">
          <CommissionAttachmentSelector
            dealId={dealId}
            selectedIds={attachments[COMMISSION_ATTACHMENT_KEY] || []}
            onChange={ids => onAttachmentsChange(COMMISSION_ATTACHMENT_KEY, ids)}
            isRequired={displayValue === true}
            documentType={displayValue === true ? DOC_TYPE_COMMISSION_AGREEMENT : DOC_TYPE_OTHER}
            uploadTitle={displayValue === true
              ? 'If yes, upload a commission agreement (required).'
              : 'Upload Supporting Documentation'}
            respectDocumentCategory
            showAllFileTypes
            allowEmailFiles
          />
        </div>
      )}

    </>
  );
};

const escalationFixedValueType = PropTypes.shape({
  rent_escalation_dollar: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  rent_escalation_percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  rent_escalation_is_percent: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
});

const escalationScheduleRowType = PropTypes.shape({
  months: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  is_percent: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  rent_dollar: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  rent_percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
});

const fieldValueType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.number,
  PropTypes.bool,
  PropTypes.shape({ years: PropTypes.number, months: PropTypes.number }),
  escalationFixedValueType,
  PropTypes.arrayOf(escalationScheduleRowType),
]);

const fieldShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.string,
  fieldType: PropTypes.string,
  isEditable: PropTypes.bool,
  isLocked: PropTypes.bool,
  currentValue: fieldValueType,
  outlierLabel: PropTypes.string,
  outlierMessage: PropTypes.string,
  outlierMin: PropTypes.number,
  outlierMax: PropTypes.number,
});

FieldRow.propTypes = {
  field: fieldShape.isRequired,
  fieldValues: PropTypes.objectOf(fieldValueType).isRequired,
  onChange: PropTypes.func.isRequired,
  onBooleanChange: PropTypes.func.isRequired,
  onEscalationModeChange: PropTypes.func,
  onEscalationScheduleCommit: PropTypes.func,
  onDateCommit: PropTypes.func,
  onBlur: PropTypes.func.isRequired,
  dealId: PropTypes.number,
  attachments: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
  onAttachmentsChange: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  requiredErrorMessage: PropTypes.string,
  emptyReason: PropTypes.string,
  isRequired: PropTypes.bool,
  outlierRanges: PropTypes.objectOf(PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
  })).isRequired,
};

FieldRow.defaultProps = {
  dealId: null,
  isSaving: false,
  onEscalationModeChange: () => {},
  onEscalationScheduleCommit: () => {},
  onDateCommit: () => {},
  requiredErrorMessage: null,
  emptyReason: null,
  isRequired: false,
};

FreeRentInput.propTypes = {
  value: PropTypes.shape({ years: PropTypes.number, months: PropTypes.number }),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
FreeRentInput.defaultProps = { value: null, disabled: false };

EscalationTypeToggle.propTypes = {
  isPercent: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

LeaseEscalationInput.propTypes = {
  value: escalationFixedValueType,
  onChange: PropTypes.func.isRequired,
};

LeaseEscalationSchedule.propTypes = {
  rows: PropTypes.arrayOf(escalationScheduleRowType).isRequired,
  onChange: PropTypes.func.isRequired,
  onCommit: PropTypes.func.isRequired,
};

LeaseEscalationEditor.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    escalationFixedValueType,
    PropTypes.arrayOf(escalationScheduleRowType),
  ]),
  savedValues: PropTypes.shape({
    fixed: escalationFixedValueType,
    custom: PropTypes.arrayOf(escalationScheduleRowType),
  }),
  onChange: PropTypes.func.isRequired,
  onScheduleCommit: PropTypes.func.isRequired,
};

RadioGroup.propTypes = {
  value: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
RadioGroup.defaultProps = { value: null, disabled: false };

export default FieldRow;
